import { supabase } from '../lib/supabase';

export interface MatchPointsData {
  matchId: string;
  homeTeamId: string;
  awayTeamId: string;
  homeResult: 'win' | 'draw' | 'loss';
  awayResult: 'win' | 'draw' | 'loss';
  homePoints: number;
  awayPoints: number;
  homeGoalsFor: number;
  homeGoalsAgainst: number;
  awayGoalsFor: number;
  awayGoalsAgainst: number;
}

export interface TeamStanding {
  team_id: string;
  team_name: string;
  matches_played: number;
  wins: number;
  draws: number;
  losses: number;
  goals_for: number;
  goals_against: number;
  goal_difference: number;
  total_points: number;
  position: number;
}

export interface MatchDetails {
  id: string;
  name: string;
  home_team_name: string;
  away_team_name: string;
  match_date: string;
  home_score: number;
  away_score: number;
  home_penalty_score?: number;
  away_penalty_score?: number;
  status: 'scheduled' | 'live' | 'finished';
}

export interface TournamentStandingsConfig {
  id: string;
  tournament_id: string;
  season_id?: string;
  format_type: string;
  stage_name?: string;
  default_win_points: number;
  default_draw_points: number;
  default_loss_points: number;
  tie_breaker_order?: string[];
  match_mapping?: string[]; // JSON array of match IDs
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface CreateOrUpdateTournamentStandingsConfig {
  id?: string;
  tournament_id: string;
  season_id?: string;
  format_type: string;
  stage_name?: string;
  default_win_points: number;
  default_draw_points: number;
  default_loss_points: number;
  tie_breaker_order?: string[];
  match_mapping?: string[]; // JSON array of match IDs
  is_active: boolean;
}

class MatchPointsService {
  /**
   * Create match points for both teams
   */
  async createMatchPoints(data: MatchPointsData): Promise<void> {
    try {
      console.log('📊 Creating match points:', data.matchId);

      // Calculate goal differences
      const homeGoalDifference = data.homeGoalsFor - data.homeGoalsAgainst;
      const awayGoalDifference = data.awayGoalsFor - data.awayGoalsAgainst;

      // Prepare data for both teams
      const matchPointsData = [
        {
          match_id: data.matchId,
          team_id: data.homeTeamId,
          result: data.homeResult,
          points_awarded: data.homePoints,
          goals_for: data.homeGoalsFor,
          goals_against: data.homeGoalsAgainst,
          goal_difference: homeGoalDifference,
          created_by: (await supabase.auth.getUser()).data.user?.id
        },
        {
          match_id: data.matchId,
          team_id: data.awayTeamId,
          result: data.awayResult,
          points_awarded: data.awayPoints,
          goals_for: data.awayGoalsFor,
          goals_against: data.awayGoalsAgainst,
          goal_difference: awayGoalDifference,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }
      ];

      const { error } = await supabase
        .from('match_points')
        .insert(matchPointsData);

      if (error) {
        console.error('❌ Error creating match points:', error);
        throw error;
      }

      console.log('✅ Match points created successfully');
    } catch (error) {
      console.error('❌ Create match points error:', error);
      throw error;
    }
  }

  /**
   * Get computed standings for a tournament
   */
  async getStandingsByTournament(tournamentId: string, seasonId?: string): Promise<TeamStanding[]> {
    try {
      console.log('📊 Getting standings for tournament:', tournamentId, seasonId);

      // Build the query
      let query = supabase
        .from('match_points')
        .select(`
          team_id,
          teams!inner(name),
          result,
          points_awarded,
          goals_for,
          goals_against,
          goal_difference,
          matches!inner(
            tournament_id,
            season_id
          )
        `)
        .eq('matches.tournament_id', tournamentId);

      if (seasonId) {
        query = query.eq('matches.season_id', seasonId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching standings:', error);
        throw error;
      }

      // Aggregate the data
      const standingsMap = new Map<string, TeamStanding>();

      data?.forEach((row: any) => {
        const teamId = row.team_id;
        const teamName = row.teams.name;

        if (!standingsMap.has(teamId)) {
          standingsMap.set(teamId, {
            team_id: teamId,
            team_name: teamName,
            matches_played: 0,
            wins: 0,
            draws: 0,
            losses: 0,
            goals_for: 0,
            goals_against: 0,
            goal_difference: 0,
            total_points: 0,
            position: 0
          });
        }

        const standing = standingsMap.get(teamId)!;
        standing.matches_played += 1;
        standing.goals_for += row.goals_for;
        standing.goals_against += row.goals_against;
        standing.goal_difference += row.goal_difference;
        standing.total_points += row.points_awarded;

        switch (row.result) {
          case 'win':
            standing.wins += 1;
            break;
          case 'draw':
            standing.draws += 1;
            break;
          case 'loss':
            standing.losses += 1;
            break;
        }
      });

      // Convert to array and sort
      const standings = Array.from(standingsMap.values());
      
      // Sort by points, then goal difference, then goals for
      standings.sort((a, b) => {
        if (b.total_points !== a.total_points) {
          return b.total_points - a.total_points;
        }
        if (b.goal_difference !== a.goal_difference) {
          return b.goal_difference - a.goal_difference;
        }
        return b.goals_for - a.goals_for;
      });

      // Assign positions
      standings.forEach((standing, index) => {
        standing.position = index + 1;
      });

      console.log('✅ Standings computed successfully:', standings.length, 'teams');
      return standings;
    } catch (error) {
      console.error('❌ Get standings error:', error);
      throw error;
    }
  }

  /**
   * Update existing match points
   */
  async updateMatchPoints(matchId: string, data: Omit<MatchPointsData, 'matchId'>): Promise<void> {
    try {
      console.log('📊 Updating match points:', matchId);

      // First delete existing points
      await this.deleteMatchPoints(matchId);

      // Then create new points
      await this.createMatchPoints({
        matchId,
        ...data
      });

      console.log('✅ Match points updated successfully');
    } catch (error) {
      console.error('❌ Update match points error:', error);
      throw error;
    }
  }

  /**
   * Delete match points (removes both team rows)
   */
  async deleteMatchPoints(matchId: string): Promise<void> {
    try {
      console.log('📊 Deleting match points:', matchId);

      const { error } = await supabase
        .from('match_points')
        .delete()
        .eq('match_id', matchId);

      if (error) {
        console.error('❌ Error deleting match points:', error);
        throw error;
      }

      console.log('✅ Match points deleted successfully');
    } catch (error) {
      console.error('❌ Delete match points error:', error);
      throw error;
    }
  }

  /**
   * Get all match points for the current organization
   */
  async getAllMatchPoints(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('match_points')
        .select('match_id')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching all match points:', error);
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllMatchPoints:', error);
      throw error;
    }
  }

  /**
   * Get match points for a specific match
   */
  async getMatchPoints(matchId: string): Promise<any[]> {
    try {
      console.log('📊 Getting match points:', matchId);

      const { data, error } = await supabase
        .from('match_points')
        .select(`
          *,
          teams(name),
          matches(home_team_id, away_team_id)
        `)
        .eq('match_id', matchId);

      if (error) {
        console.error('❌ Error fetching match points:', error);
        throw error;
      }

      console.log('✅ Match points fetched successfully');
      return data || [];
    } catch (error) {
      console.error('❌ Get match points error:', error);
      throw error;
    }
  }

  /**
   * Create or update tournament standings configuration
   */
  async createOrUpdateStandingsConfig(config: CreateOrUpdateTournamentStandingsConfig): Promise<TournamentStandingsConfig> {
    try {
      console.log('📊 Creating/updating standings config:', config.tournament_id);

      const { data, error } = await supabase
        .from('tournament_standings_config')
        .upsert({
          ...config,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating/updating standings config:', error);
        throw error;
      }

      console.log('✅ Standings config created/updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Create/update standings config error:', error);
      throw error;
    }
  }

  /**
   * Get tournament standings configuration
   */
  async getStandingsConfig(tournamentId: string, seasonId?: string): Promise<TournamentStandingsConfig | null> {
    try {
      console.log('📊 Getting standings config:', tournamentId, seasonId);

      let query = supabase
        .from('tournament_standings_config')
        .select('*')
        .eq('tournament_id', tournamentId);

      if (seasonId) {
        query = query.eq('season_id', seasonId);
      } else {
        // Get the first active configuration for this tournament
        query = query.eq('is_active', true);
      }

      const { data, error } = await query.order('created_at', { ascending: false }).limit(1).single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          return null;
        }
        console.error('❌ Error fetching standings config:', error);
        throw error;
      }

      console.log('✅ Standings config fetched successfully');
      return data;
    } catch (error) {
      console.error('❌ Get standings config error:', error);
      throw error;
    }
  }

  /**
   * Get all tournament standings configurations
   */
  async getAllStandingsConfigs(tournamentId: string): Promise<TournamentStandingsConfig[]> {
    try {
      console.log('📊 Getting all standings configs:', tournamentId);

      const { data, error } = await supabase
        .from('tournament_standings_config')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching all standings configs:', error);
        throw error;
      }

      console.log('✅ All standings configs fetched successfully');
      return data || [];
    } catch (error) {
      console.error('❌ Get all standings configs error:', error);
      throw error;
    }
  }

  /**
   * Update tie-breaker order for a configuration
   */
  async updateTieBreakerOrder(configId: string, tieBreakerOrder: string[]): Promise<TournamentStandingsConfig> {
    try {
      console.log('📊 Updating tie-breaker order:', configId, tieBreakerOrder);

      const { data, error } = await supabase
        .from('tournament_standings_config')
        .update({ 
          tie_breaker_order: tieBreakerOrder,
          updated_at: new Date().toISOString()
        })
        .eq('id', configId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating tie-breaker order:', error);
        throw error;
      }

      console.log('✅ Tie-breaker order updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Update tie-breaker order error:', error);
      throw error;
    }
  }

  async updateMatchMapping(configId: string, matchIds: string[]): Promise<TournamentStandingsConfig> {
    try {
      console.log('📊 Updating match mapping:', configId, matchIds);

      const { data, error } = await supabase
        .from('tournament_standings_config')
        .update({ 
          match_mapping: matchIds,
          updated_at: new Date().toISOString()
        })
        .eq('id', configId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating match mapping:', error);
        throw error;
      }

      console.log('✅ Match mapping updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Update match mapping error:', error);
      throw error;
    }
  }

  /**
   * Get tournament standings for a specific tournament and season
   * Only includes teams from matches that are mapped to active configs
   */
  async getTournamentStandings(tournamentId: string, seasonId?: string): Promise<TeamStanding[]> {
    try {
      console.log('📊 Getting tournament standings for tournament:', tournamentId, seasonId);

      // First get the tournament configurations to get match_mapping and points
      let configQuery = supabase
        .from('tournament_standings_config')
        .select('id, match_mapping, default_win_points, default_draw_points, default_loss_points')
        .eq('tournament_id', tournamentId)
        .eq('is_active', true);

      if (seasonId) {
        configQuery = configQuery.eq('season_id', seasonId);
      }

      const { data: configs, error: configError } = await configQuery;

      if (configError) {
        console.error('❌ Error fetching tournament configs for standings:', configError);
        throw configError;
      }

      if (!configs || configs.length === 0) {
        console.log('📊 No tournament configs found for standings');
        return [];
      }

      console.log('📊 Found tournament configs for standings:', configs);

      // Collect all mapped match IDs from all active configs
      const allMappedMatchIds = new Set<string>();
      configs.forEach(config => {
        if (config.match_mapping && Array.isArray(config.match_mapping)) {
          config.match_mapping.forEach((matchId: string) => {
            allMappedMatchIds.add(matchId);
          });
        }
      });

      const mappedMatchIdsArray = Array.from(allMappedMatchIds);
      console.log('📊 Total mapped matches across all configs:', mappedMatchIdsArray.length);

      if (mappedMatchIdsArray.length === 0) {
        console.log('📊 No mapped matches found for standings');
        return [];
      }

      // Get match points only for mapped matches
      const { data: matchPointsData, error: matchPointsError } = await supabase
        .from('match_points')
        .select(`
          match_id,
          team_id,
          result,
          points_awarded,
          goals_for,
          goals_against,
          goal_difference,
          teams!inner(name)
        `)
        .in('match_id', mappedMatchIdsArray);

      if (matchPointsError) {
        console.error('❌ Error fetching match points for tournament standings:', matchPointsError);
        throw matchPointsError;
      }

      console.log('📊 Found match points for tournament standings:', matchPointsData.length);

      if (!matchPointsData || matchPointsData.length === 0) {
        console.log('📊 No match points found for tournament standings');
        return [];
      }

      // Calculate standings
      const teamStandingsMap = new Map<string, TeamStanding>();

      matchPointsData.forEach((mp: any) => {
        const teamId = mp.team_id;
        const teamName = mp.teams.name;

        if (!teamStandingsMap.has(teamId)) {
          teamStandingsMap.set(teamId, {
            team_id: teamId,
            team_name: teamName,
            matches_played: 0,
            wins: 0,
            draws: 0,
            losses: 0,
            goals_for: 0,
            goals_against: 0,
            goal_difference: 0,
            total_points: 0,
            position: 0
          });
        }

        const teamStanding = teamStandingsMap.get(teamId)!;
        teamStanding.matches_played += 1;
        teamStanding.goals_for += mp.goals_for;
        teamStanding.goals_against += mp.goals_against;
        teamStanding.goal_difference += mp.goal_difference;
        
        // Recalculate points based on tournament configuration instead of using stored points_awarded
        let pointsToAdd = 0;
        const firstConfig = configs[0]; // Use the first config's points settings
        switch (mp.result) {
          case 'win':
            teamStanding.wins += 1;
            pointsToAdd = firstConfig.default_win_points || 0;
            break;
          case 'draw':
            teamStanding.draws += 1;
            pointsToAdd = firstConfig.default_draw_points || 0;
            break;
          case 'loss':
            teamStanding.losses += 1;
            pointsToAdd = firstConfig.default_loss_points || 0;
            break;
        }
        teamStanding.total_points += pointsToAdd;
      });

      // Convert to array and sort
      const standings = Array.from(teamStandingsMap.values());
      
      // Sort by points, then goal difference, then goals for
      standings.sort((a, b) => {
        if (b.total_points !== a.total_points) {
          return b.total_points - a.total_points;
        }
        if (b.goal_difference !== a.goal_difference) {
          return b.goal_difference - a.goal_difference;
        }
        return b.goals_for - a.goals_for;
      });

      // Assign positions
      standings.forEach((standing, index) => {
        standing.position = index + 1;
      });

      console.log('✅ Tournament standings computed successfully:', standings.length, 'teams');
      return standings;
    } catch (error) {
      console.error('❌ Get tournament standings error:', error);
      throw error;
    }
  }

  /**
   * Get group standings for a tournament (grouped by stage_name from tournament_standings_config)
   * Only includes teams from matches that are mapped to the specific config
   */
  async getGroupStandings(tournamentId: string, seasonId?: string): Promise<Map<string, TeamStanding[]>> {
    try {

      // First get the tournament configurations to get stage_name, match_mapping and points
      let configQuery = supabase
        .from('tournament_standings_config')
        .select('id, stage_name, match_mapping, default_win_points, default_draw_points, default_loss_points')
        .eq('tournament_id', tournamentId)
        .eq('is_active', true);

      if (seasonId) {
        configQuery = configQuery.eq('season_id', seasonId);
      }

      const { data: configs, error: configError } = await configQuery;

      if (configError) {
        console.error('❌ Error fetching tournament configs for group standings:', configError);
        throw configError;
      }

      if (!configs || configs.length === 0) {
        return new Map();
      }


      // Filter configs to only include group_stage format
      const groupConfigs = configs.filter(config => 
        config.stage_name && 
        !config.stage_name.toLowerCase().includes('semifinal') && 
        !config.stage_name.toLowerCase().includes('final') &&
        !config.stage_name.toLowerCase().includes('knockout')
      );

      if (groupConfigs.length === 0) {
        return new Map();
      }


      // Group standings by stage_name from configs
      const groupStandingsMap = new Map<string, Map<string, TeamStanding>>();

      // Process each group config separately
      for (const config of groupConfigs) {
        const groupName = config.stage_name || 'General Group';
        
        // Get mapped match IDs for this config
        let mappedMatchIds: string[] = [];
        if (config.match_mapping && Array.isArray(config.match_mapping)) {
          mappedMatchIds = config.match_mapping;
        }


        if (mappedMatchIds.length === 0) {
          continue;
        }

        // Get match points only for mapped matches
        const { data: matchPointsData, error: matchPointsError } = await supabase
          .from('match_points')
          .select(`
            match_id,
            team_id,
            result,
            points_awarded,
            goals_for,
            goals_against,
            goal_difference,
            teams!inner(name)
          `)
          .in('match_id', mappedMatchIds);

        if (matchPointsError) {
          console.error(`❌ Error fetching match points for group "${groupName}":`, matchPointsError);
          continue; // Skip this group but continue with others
        }


        if (!matchPointsData || matchPointsData.length === 0) {
          continue;
        }

        // Initialize group teams map
        if (!groupStandingsMap.has(groupName)) {
          groupStandingsMap.set(groupName, new Map());
        }

        const groupTeams = groupStandingsMap.get(groupName)!;

        // Process match points for this group
        matchPointsData.forEach((mp: any) => {
          const teamId = mp.team_id;
          const teamName = mp.teams.name;

          if (!groupTeams.has(teamId)) {
            groupTeams.set(teamId, {
              team_id: teamId,
              team_name: teamName,
              matches_played: 0,
              wins: 0,
              draws: 0,
              losses: 0,
              goals_for: 0,
              goals_against: 0,
              goal_difference: 0,
              total_points: 0,
              position: 0
            });
          }

          const teamStanding = groupTeams.get(teamId)!;
          teamStanding.matches_played += 1;
          teamStanding.goals_for += mp.goals_for;
          teamStanding.goals_against += mp.goals_against;
          teamStanding.goal_difference += mp.goal_difference;
          
          // Recalculate points based on tournament configuration instead of using stored points_awarded
          let pointsToAdd = 0;
          switch (mp.result) {
            case 'win':
              teamStanding.wins += 1;
              pointsToAdd = (config as any).default_win_points || 0;
              break;
            case 'draw':
              teamStanding.draws += 1;
              pointsToAdd = (config as any).default_draw_points || 0;
              break;
            case 'loss':
              teamStanding.losses += 1;
              pointsToAdd = (config as any).default_loss_points || 0;
              break;
          }
          teamStanding.total_points += pointsToAdd;
        });
      }

      // Convert to final format and sort each group
      const finalGroupStandings = new Map<string, TeamStanding[]>();

      groupStandingsMap.forEach((teamMap, groupName) => {
        const standings = Array.from(teamMap.values());
        
        // Sort by points, then goal difference, then goals for
        standings.sort((a, b) => {
          if (b.total_points !== a.total_points) {
            return b.total_points - a.total_points;
          }
          if (b.goal_difference !== a.goal_difference) {
            return b.goal_difference - a.goal_difference;
          }
          return b.goals_for - a.goals_for;
        });

        // Assign positions
        standings.forEach((standing, index) => {
          standing.position = index + 1;
        });

        finalGroupStandings.set(groupName, standings);
      });

      return finalGroupStandings;
    } catch (error) {
      console.error('❌ Get group standings error:', error);
      throw error;
    }
  }

  /**
   * Get matches from match points for a specific tournament and season
   * Uses inner join approach: matches table filtered by tournament/season, then joined with match_points
   */
  async getMatchesFromMatchPoints(tournamentId: string, seasonId?: string): Promise<MatchDetails[]> {
    try {
      console.log('📊 Getting matches from match points:', tournamentId, seasonId);

      // Use a single query with inner join to get matches that exist in both tables
      let query = supabase
        .from('matches')
        .select(`
          id,
          start_date,
          tournament_id,
          season_id,
          home_score,
          away_score,
          home_penalty_score,
          away_penalty_score,
          status,
          home_team:home_team_id(
            id,
            name,
            logo
          ),
          away_team:away_team_id(
            id,
            name,
            logo
          )
        `)
        .eq('tournament_id', tournamentId);

      if (seasonId) {
        query = query.eq('season_id', seasonId);
      }

      // Execute the query to get matches for this tournament/season
      const { data: matchesData, error: matchesError } = await query;

      if (matchesError) {
        console.error('❌ Error fetching matches:', matchesError);
        throw matchesError;
      }

      console.log('📊 Found matches for tournament/season:', matchesData?.length || 0);

      if (!matchesData || matchesData.length === 0) {
        console.log('📊 No matches found for tournament/season');
        return [];
      }

      // Extract match IDs
      const matchIds = matchesData.map(match => match.id);
      console.log('📊 Match IDs to check in match points:', matchIds);

      // Now check which of these matches exist in match_points table
      const { data: matchPointsData, error: matchPointsError } = await supabase
        .from('match_points')
        .select('match_id')
        .in('match_id', matchIds);

      if (matchPointsError) {
        console.error('❌ Error fetching match points:', matchPointsError);
        throw matchPointsError;
      }

      console.log('📊 Match points data:', matchPointsData);

      if (!matchPointsData || matchPointsData.length === 0) {
        console.log('📊 No match points found for these matches');
        return [];
      }

      // Extract unique match IDs that exist in match points
      const validMatchIds = [...new Set(matchPointsData.map(item => item.match_id))];
      console.log('📊 Valid match IDs in match points:', validMatchIds);

      // Filter the original matches data to only include those that exist in match points
      const filteredMatches = matchesData.filter(match => validMatchIds.includes(match.id));

      const matches: MatchDetails[] = filteredMatches.map(match => ({
        id: match.id,
        name: `${(match.home_team as any)?.name || 'Home Team'} vs ${(match.away_team as any)?.name || 'Away Team'}`,
        home_team_name: (match.home_team as any)?.name || 'Home Team',
        away_team_name: (match.away_team as any)?.name || 'Away Team',
        match_date: match.start_date,
        home_score: match.home_score || 0,
        away_score: match.away_score || 0,
        home_penalty_score: match.home_penalty_score,
        away_penalty_score: match.away_penalty_score,
        status: match.status || 'scheduled'
      }));

      console.log('✅ Matches fetched successfully:', matches.length, matches);
      return matches;
    } catch (error) {
      console.error('❌ Get matches from match points error:', error);
      throw error;
    }
  }

  /**
   * Set a configuration as active
   */
  async setActiveConfig(configId: string, tournamentId: string): Promise<void> {
    try {
      console.log('📊 Setting active config:', configId, tournamentId);

      // Set the specified config as active
      const { error } = await supabase
        .from('tournament_standings_config')
        .update({ is_active: true })
        .eq('id', configId)
        .eq('tournament_id', tournamentId);

      if (error) {
        console.error('❌ Error setting active config:', error);
        throw error;
      }

      console.log('✅ Active config set successfully');
    } catch (error) {
      console.error('❌ Set active config error:', error);
      throw error;
    }
  }
}

export const matchPointsService = new MatchPointsService();
